import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@tanium/react-box';
import { Notification } from '@tanium/react-persistent-notifications';
import { AsyncStateData, AsyncStatus } from '../hooks/useAsync';

interface ErrorBase {
  message: string;
}

interface AsyncFailureProps {
  error: any;
  message?: string;
}

export const AsyncFailure: React.VFC<AsyncFailureProps> = ({ error, message }) => {
  const { t } = useTranslation();
  if (message) {
    return (
      <Box>
        <Notification role="alert" type="error">
          {message}
        </Notification>
      </Box>
    );
  }

  const requestError = error as unknown as ErrorBase;
  const errorMessage =
    (requestError && requestError.message) ||
    t('The Account Support Portal is temporarily unavailable or undergoing maintenance.');
  return (
    <Box>
      <Notification role="alert" type="error">
        {errorMessage}
      </Notification>
    </Box>
  );
};

interface AsyncSuspenseProps {
  /*
   * See `useAsync.AsyncState`
   */
  state: AsyncStateData<any, Error>;

  /*
   * Element to render when the request has succeeeded.
   */
  children: JSX.Element;

  /*
   * Element to render if the request fails. If a string is passed, report the error message in a error notification box.
   * By default, reports a generic error message in a error notification box.
   */
  failure?: JSX.Element | string | undefined;

  /*
   * Element to render while the request is pending.
   */
  loading?: JSX.Element | undefined;
}

export const AsyncSuspense: React.FC<AsyncSuspenseProps> = ({ state, children, failure, loading }) => {
  const { status } = state;

  if (status === AsyncStatus.Pending) {
    if (loading) {
      return loading;
    }
    return children;
  }

  if (status === AsyncStatus.Failure) {
    return typeof failure === 'object' ? (
      failure
    ) : (
      <AsyncFailure error={state.error} message={failure as string}></AsyncFailure>
    );
  }

  if (status === AsyncStatus.Success) {
    return children;
  }

  return <Box></Box>;
};
